import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ErrorExitDialog } from '@/components/ErrorExitDialog'
import { LoadingOverlay } from '@/components/ui/LoadingOverlay'
import { addPaymentBreadcrumb, setPaymentContext } from '@/lib/sentry'
import { apiPostProductPaymentProcess } from '@/payment/api'
import { ROUTES } from '@/router/routes'

import { parseEntryContext } from './lib/parseEntryContext'

/**
 * PG redirect 결과를 백엔드에 저장하는 중계 페이지.
 *
 * 진입 즉시:
 *  1. URL 쿼리 파싱 → 토스 success / 네이버 success / 네이버 fail / 인식 불가 분기
 *  2. sessionStorage에서 paymentId 복원 (PaymentStep에서 결제 직전 저장)
 *  3. (네이버 한정) amount도 sessionStorage에서 복원 — returnUrl에 amount 없음
 *  4. POST /product-payments/process 호출
 *  5. 성공 → `/sealed-vinyl/result?key=UUID`로 replace navigate
 *  6. 네이버 실패 → `/sealed-vinyl/result?{raw query}`로 replace navigate (결과 화면에서 처리)
 *
 * 토스 failUrl은 PG 호출 시 `/sealed-vinyl/result`로 직접 지정 — 이 페이지를 거치지 않음.
 */
export default function SealedVinylProcessPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [postFailed, setPostFailed] = useState(false)
  const ranRef = useRef(false)

  const parsed = useMemo(() => parseEntryContext(searchParams), [searchParams])

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    setPaymentContext({ step: 'process' })

    if (parsed.type === 'naverFail') {
      // 취소/실패 시 오류 모달 없이 결제 페이지로 복귀 — resume 이 sessionStorage 에 있어 PaymentStep 복원됨
      addPaymentBreadcrumb('결제 중계: 네이버 취소/실패 → 결제 화면 복귀')
      navigate(ROUTES.SEALED_VINYL, { replace: true })
      return
    }

    if (parsed.type === 'process') {
      addPaymentBreadcrumb('결제 중계(process) 저장 시도', {
        paymentMethodType: parsed.body.paymentMethodType,
        paymentId: parsed.body.paymentId,
      })
      apiPostProductPaymentProcess(parsed.body)
        .then(({ key }) => navigate(`${ROUTES.SEALED_VINYL_RESULT}?key=${encodeURIComponent(key)}`, { replace: true }))
        .catch(() => setPostFailed(true))
    }
  }, [navigate, parsed])

  // 결과 확인 불가(인식 불가·컨텍스트 유실은 렌더 단계에서 즉시 / process 실패는 async 후) — 안내 모달 후 앱 이탈
  if (parsed.type === 'unknown' || parsed.type === 'missingContext' || postFailed) {
    return <ErrorExitDialog />
  }

  return <LoadingOverlay />
}
